import * as React from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  useRouterState,
} from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { logspot as Logspot } from '@logspot/web';
import { useEffect } from 'react';
import type { SetAtom } from '@/types';
import type { useSession } from '@/lib/auth-client';
import { useAtomValue, type SetStateAction } from 'jotai';
import { themeAtom } from '@/state/atoms';
import { Loader } from '@/components/ui/loader';
import { IS_PRODUCTION_DEPLOY } from '@/lib/deploy-env';
import { Toaster } from '@/components/ui/sonner';
import { OfflineCheck } from '@/components/offline-check';

type SessionData = ReturnType<typeof useSession>['data'];

type RouterContext = {
  isAuthenticated: () => boolean;
  isSessionPending: boolean;
  session: SessionData;
  queryClient: QueryClient;
  projectId: string | null;
  setProjectId: SetAtom<[SetStateAction<string | null>], void>;
  userRole?: string;
  platformRole?: string;
};

// Add logspot tracking to production deployment environment
if (IS_PRODUCTION_DEPLOY) {
  Logspot.init({
    publicKey: 'pk_zNIoeGFrWZeNpRq56OKU8tJFvcN628LbN-DSnbBw',
    cookieDomain: '.logspot.io',
  });
}

const TanStackRouterDevtools =
  import.meta.env.MODE === 'production'
    ? () => null // Render nothing in production or stage environments
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      );

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const theme = useAtomValue(themeAtom);
  // A public share sets light or dark from its own colors, so the visitor's app theme stays out of it.
  const onShare = useRouterState({
    select: (state) => state.location.pathname.startsWith('/shares/'),
  });

  useEffect(() => {
    if (onShare) return;
    const root = window.document.documentElement;

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      const prevTheme = systemTheme === 'dark' ? 'light' : 'dark';
      root.classList.remove(prevTheme);
      root.classList.add(systemTheme);
      return;
    }

    const prevTheme = theme === 'dark' ? 'light' : 'dark';
    root.classList.remove(prevTheme);
    root.classList.add(theme);
  }, [theme, onShare]);

  useEffect(() => {
    Logspot.track({
      event: 'App Opened',
    });
  }, []);

  useEffect(() => {
    if (window.location.pathname.startsWith('/shares/')) return;
    // @ts-ignore
    window.$crisp = [];
    // @ts-ignore
    window.CRISP_WEBSITE_ID = 'cda388a8-26d8-4726-80c4-81623fb37e17';

    (function () {
      const d = document;
      const s = d.createElement('script');

      s.src = 'https://client.crisp.chat/l.js';
      // @ts-ignore
      s.async = 1;
      d.getElementsByTagName('head')[0]?.appendChild(s);
    })();
  }, []);

  return (
    <>
      {/*
        Theme-aware root wrapper. Every route renders inside this div, so
        bg-background and text-foreground apply once and cascade everywhere.
        Pages don't need to set their own bg/text — _side-layout's
        SidebarInset, _basic-layout's wrapper, sign-in/onboarding/etc all
        inherit through here.
      */}
      <div className="bg-background text-foreground min-h-dvh">
        <Outlet />
      </div>
      <React.Suspense
        fallback={
          <div className="grid size-auto min-h-40">
            <Loader />
          </div>
        }
      >
        <ReactQueryDevtools buttonPosition="bottom-right" />
        <TanStackRouterDevtools position="bottom-right" />
      </React.Suspense>
      <Toaster richColors position="top-right" closeButton />
      <OfflineCheck />
    </>
  );
}
